// Work step data
export interface WorkStep {
    id: number;
    typeOfCoating: string;
    unit: string;
    amount: number;
    surfaceArea: number;
    tons: number;
    unitPrice: number;
    priceC: number;
    isGroupHeader?: boolean; // True if this is a group header row
}

// Role permissions for site specific access
export interface RolePermission {
    roleId: number;
    roleName: string;
    accessLevel: number; // 0 = no Access, 1 = read only, 2 = read/write
}

// File upload with custom header
export interface FileUploadItem {
    id: string; // Unique identifier
    file: File;
    fileHeader: string; // Custom description
}

// Existing file metadata
export interface ExistingFileInfo {
    id: number; // Database id
    name: string;
    size: number;
    extension: string;
    fileHeader: string | null;
    fileType: number;
    hasThumbnail: boolean;
}

// Work step form data
export interface WorkStepForm {
    operation: string;
    amount: number;
    surfaceArea: number;
    tons: number;
    anotherName: string;
    otherUnit: string;
    otherUnitPrice?: number;
    description: string;
}

// Main form data structure for object inspection
export interface ObjectData {
    id: number;
    wkt: string;
    name: string;
    streetName: string;
    
    // Dropdown fields - ids and strings 
    job: string; // contractId
    status: string; // stateId
    type: string; // strengthId
    typeDesc: string; // type description from lookup_data
    qualityControl: string; // qualityControlId
    qualityControl2: string; // qualityControlId2
    warrantyFollowUp: string; // guaranteeFollowId
    officeUser: string;
    pavementActionType: string;
    pavementGroundType: string;
    
    // Text fields
    drawingType: string;
    orderName: string; // backend - orderNumber
    ordererReferenceNumber: string;
    networkName: string; // backend - networkNumber
    measures: string; // backend - typeComment
    lanes: string;
    levelOfReadiness: string; // backend - goals
    description: string;
    qualityControlComments: string; // backend - extraText1
    warrantyFollowUpComments: string; // backend - extraText2
    invoiceNumber: string;
    invoicingPvm: string; 
    implementedComment: string;
    measurementPersons: string;
    contractorWorkNumber: string;
    targetContract: string; // For copy operation
    newObjectName: string; // For copy operation
    
    // Date fields
    plannedYear: Date | null; // Derived from planneddate
    pvm: Date | null; // planneddate in backend
    agreedPvm: Date | null; // planneddate in backend 
    completedPvm: Date | null; // readydate in backend
    approvedPvm: Date | null; // approveddate in backend
    measurementDate: Date | null;
    modifier: string | null;
    modified: Date | null;
    checkedToInvoiceDate: Date | null;
    checkedToInvoiceUserName: string | null;
    vehicleRelocationNoticeAdded: Date | null;
    vehicleRelocationNoticeStart: Date | null;
    vehicleRelocationNoticeEnd: Date | null;
    allowedToInvoiceDate: Date | null;
    
    // Boolean fields - 0/1 in backend
    checkedToInvoice: boolean;
    signingLocked: boolean;
    roadMarkings: boolean;
    publishToApi: boolean;
    invoiced: boolean;
    allowedToInvoice: boolean;

    startDistance: number | null;
    endDistance: number | null;
    objectLength: number | null;
    
    // User and location objects
    subscriber: { id: number; name: string; email?: string; organization1?: string; organization2?: string } | null;
    foreman: { id: number; name: string; email?: string } | null;
    contractorForeman: { id: number; name: string; email?: string } | null;
    projectManager: { id: number; name: string; email?: string } | null;
    drillLocation: DrillLocation | null;
    imageAttachment: File | null; 
    
    // Arrays
    rolePermissions: RolePermission[];
    existingFiles: ExistingFileInfo[];
    existingQcFiles: ExistingFileInfo[];
    fileIdsToKeep: number[];
    qcFileIdsToKeep: number[];
    attachedFiles: FileUploadItem[]; // For upload
    coatingResearchReport: FileUploadItem[]; // qc files
    plannedWorkSteps: WorkStep[]; // Not in base geometry
    
    // Form specific
    userRightsType: 'site-specific' | 'general'; // From roleaccessinuse
}

export const STARA_OBJECT_FIELD_DEFAULTS: Pick<
  ObjectData,
  | "ordererReferenceNumber"
  | "qualityControl2"
  | "officeUser"
  | "pavementActionType"
  | "pavementGroundType"
  | "invoiceNumber"
  | "invoiced"
  | "allowedToInvoice"
  | "allowedToInvoiceDate"
  | "roadMarkings"
  | "publishToApi"
  | "objectLength"
  | "startDistance"
  | "endDistance"
  | "vehicleRelocationNoticeAdded"
  | "vehicleRelocationNoticeStart"
  | "vehicleRelocationNoticeEnd"
> = {
  ordererReferenceNumber: "",
  qualityControl2: "",
  officeUser: "",
  pavementActionType: "",
  pavementGroundType: "",
  invoiceNumber: "",
  invoiced: false,
  allowedToInvoice: false,
  allowedToInvoiceDate: null,
  roadMarkings: false,
  publishToApi: true,
  objectLength: null,
  startDistance: null,
  endDistance: null,
  vehicleRelocationNoticeAdded: null,
  vehicleRelocationNoticeStart: null,
  vehicleRelocationNoticeEnd: null,
};

// Drill location coordinates
export interface DrillLocation {
    id?: number;
    xcoord: number | null;
    ycoord: number | null;
}

// Objectform component props
export interface ObjectFormProps {
    opened: boolean;
    onClose: () => void;
    objectData: ObjectData | null;
    onSubmit?: (updatedObject: ObjectData) => void;
    addressSystemId?: number;
    userId?: number;
    onDrawingStateChange?: (isDrawing: boolean) => void;
}

// Tab component props
export interface TabProps {
    form: any; // Typed specifically in each component
    showSuccessAlert: boolean;
    handleSubmit: (values: ObjectData) => void;
    handleClose: () => void;
}

// Work steps tab props
export interface WorkStepsTabProps extends TabProps {
    workSteps: WorkStep[];
    setWorkSteps: (steps: WorkStep[]) => void;
    onAddWorkStep: () => void;
    onEditWorkStep: (step: WorkStep) => void;
    onDeleteWorkStep: (id: number) => void;
}

// Work step modal props
export interface WorkStepModalProps {
    opened: boolean;
    onClose: () => void;
    editingWorkStep: WorkStep | null;
    onSave: (values: WorkStepForm) => void;
}

// Unit price list data
export interface UnitPriceListData {
    objectName: string;
    availableContracts: ContractOption[];
    priceGroups: PriceGroupWithItems[];
    selectedContractId: number | null;
    hideWorkItemNumber: boolean;
    useWorkItemCode: boolean;
}

export interface ContractOption {
    id: number;
    name: string;
}

export interface PriceGroupWithItems {
    id: number;
    name: string;
    orderInGroup: number | null;
    items: PriceItemDetail[];
}

export interface PriceItemDetail {
    id: number;
    itemNumber: number;
    itemSubnumber: number;
    itemCode: string | null;
    name: string;
    unitName: string;
    unitPrice: number;
    orderInGroup: number;
}